import { Info } from "lucide-react";
import { useMemo } from "react";

import {
  DealerExceptionRankChart,
  DealerRiskScatterChart,
  buildDealerScatterPoints,
} from "../components/DealerQualityCharts";
import { DealerCreditQualityTable, DealerVelocityTable } from "../components/DealerQualityTables";
import { FilterBar } from "../components/FilterBar";
import { PageHeader } from "../components/PageHeader";
import { StepShell, getStep } from "../components/StepShell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { useDealerQualitySummary } from "../lib/api/dealer-quality";
import { useDashboardSearch } from "../lib/useDashboardSearch";

const SUBTITLE =
  "Which dealers send fast, clean deals — and which send the ones that need the most work.";

export function DealerQuality() {
  const { range, office, compare } = useDashboardSearch();
  const { data, isPending } = useDealerQualitySummary({ range, office, compare });

  const scatterPoints = useMemo(
    () =>
      data ? buildDealerScatterPoints(data.creditQuality, data.velocity.rows) : [],
    [data],
  );

  return (
    <StepShell step={getStep("dealer-quality")}>
      <PageHeader title="Dealer and Channel Quality" subtitle={SUBTITLE} />
      <FilterBar />

      {isPending || !data ? (
        <div className="space-y-4">
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Section 1 — Credit Quality Overview */}
          <Card>
            <CardHeader>
              <CardTitle>Credit Quality Overview</CardTitle>
              <CardDescription>
                One row per dealer. Office acts as an additional cut on top of dealer, so changing
                it reshapes each dealer&apos;s volume rather than swapping the list. Dealer codes are
                placeholders pending the real application source mapping.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DealerCreditQualityTable
                rows={data.creditQuality}
                breakdownByDealer={data.officeBreakdownByDealer}
                delinquencyNote={data.earlyDelinquency.note}
              />
              <p className="mt-3 text-xs text-muted-foreground">
                Select a row to see how that dealer&apos;s volume splits across offices.
              </p>
            </CardContent>
          </Card>

          {/* Section 2 — Process & Velocity */}
          <section className="space-y-4">
            {!data.velocity.dataAvailable && (
              <div
                className="flex items-start gap-3 rounded-lg border p-4 text-sm"
                style={{
                  borderColor: "color-mix(in oklch, var(--neutral-inactive) 45%, transparent)",
                  background: "var(--neutral-inactive-dim)",
                  color: "var(--neutral-inactive)",
                }}
              >
                <Info className="mt-0.5 size-4 shrink-0" />
                <p>
                  Exception rate, override rate, and decision time by dealer are captured from the
                  moment an office goes live. Select the live office or &ldquo;All Offices&rdquo; to
                  see this section — credit-quality metrics above aren&apos;t affected.
                </p>
              </div>
            )}

            {data.velocity.dataAvailable && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Exception Rate vs. Risk Tier</CardTitle>
                    <CardDescription>
                      Dot size scales with the dealer&apos;s volume. Position here is diagnostic, not
                      a verdict — no color judgment applied.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DealerRiskScatterChart points={scatterPoints} />
                    <p className="mt-3 text-xs text-muted-foreground">
                      A large dot sitting toward high exception rate and a weaker tier is the
                      relationship worth a conversation — even if its total volume looks fine on its
                      own.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Dealers Ranked by Exception Rate</CardTitle>
                    <CardDescription>
                      Ordered highest to lowest. No threshold is set on this page — that judgment
                      belongs to Automation Readiness or Manage Alerts.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DealerExceptionRankChart rows={data.velocity.rows} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Velocity Detail by Dealer</CardTitle>
                    <CardDescription>
                      Supporting look-up for whichever dealer stands out above.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DealerVelocityTable rows={data.velocity.rows} />
                  </CardContent>
                </Card>
              </>
            )}
          </section>
        </div>
      )}
    </StepShell>
  );
}
